import { useCallback, useMemo, useState } from 'react';
import {
  WorkspaceDemoAddToCampaignModal,
  WorkspaceDemoBidModal,
  WorkspaceDemoNegativeModal,
  WorkspaceDemoPortfolioModal,
  WorkspaceDemoStatusModal,
  type DemoBidMode,
} from '@/components/workspace/WorkspaceDemoActionModals';
import {
  DEMO_CAMPAIGN_MOCK_ACTION_IDS,
  groupDemoNegativesByAdGroup,
  type DemoNegativeTarget,
} from '@/api/workspaceBulkActions';

export type DemoActionModalKind =
  | 'add_negative'
  | 'change_bid'
  | 'change_status'
  | 'set_portfolio'
  | 'add_to_campaign';

type DemoModalState = {
  kind: DemoActionModalKind;
  entityLabel: string;
  selectionCount: number;
  negativeTargets?: DemoNegativeTarget[];
};

const ACTION_ID_TO_KIND: Record<string, DemoActionModalKind> = {
  add_negative: 'add_negative',
  change_bid: 'change_bid',
  change_status: 'change_status',
  set_portfolio: 'set_portfolio',
  add_to_campaign: 'add_to_campaign',
};

export function isDemoMockActionId(actionId: string): boolean {
  return actionId in ACTION_ID_TO_KIND || DEMO_CAMPAIGN_MOCK_ACTION_IDS.has(actionId);
}

export function useWorkspaceDemoActions({
  showToast,
  campaigns = [],
  adGroups = [],
  portfolios = [],
}: {
  showToast: (message: string, variant?: 'success' | 'error') => void;
  campaigns?: Array<{ id: string; name: string }>;
  adGroups?: Array<{ id: string; name: string; campaignId: string }>;
  portfolios?: Array<{ id: string; name: string }>;
}) {
  const [modal, setModal] = useState<DemoModalState | null>(null);
  const [pending, setPending] = useState(false);

  const closeModal = useCallback(() => {
    if (!pending) setModal(null);
  }, [pending]);

  const openDemoAction = useCallback(
    (
      actionId: string,
      context: {
        entityLabel: string;
        selectionCount: number;
        negativeTargets?: DemoNegativeTarget[];
      }
    ): boolean => {
      const kind = ACTION_ID_TO_KIND[actionId];
      if (!kind) return false;
      if (context.selectionCount <= 0) {
        showToast(`Select at least one ${context.entityLabel}`, 'error');
        return true;
      }
      if (kind === 'add_negative' && !(context.negativeTargets?.length ?? 0)) {
        showToast('Could not resolve selected terms for negative keywords', 'error');
        return true;
      }
      setModal({
        kind,
        entityLabel: context.entityLabel,
        selectionCount: context.selectionCount,
        negativeTargets: context.negativeTargets,
      });
      return true;
    },
    [showToast]
  );

  const finish = useCallback(
    (message: string) => {
      setPending(true);
      window.setTimeout(() => {
        showToast(message, 'success');
        setPending(false);
        setModal(null);
      }, 250);
    },
    [showToast]
  );

  const demoModals = useMemo(() => {
    if (!modal) return null;

    if (modal.kind === 'add_negative') {
      const targets = modal.negativeTargets ?? [];
      return (
        <WorkspaceDemoNegativeModal
          targets={targets}
          pending={pending}
          onCancel={closeModal}
          onConfirm={(matchType) => {
            const groups = groupDemoNegativesByAdGroup(targets);
            const termCount = groups.reduce((sum, g) => sum + g.terms.length, 0);
            finish(
              `Added ${termCount} negative keyword${termCount === 1 ? '' : 's'} (${matchType.replace('NEGATIVE_', '').toLowerCase()}) across ${groups.length} ad group${groups.length === 1 ? '' : 's'} (demo)`
            );
          }}
        />
      );
    }

    if (modal.kind === 'change_bid') {
      return (
        <WorkspaceDemoBidModal
          selectionCount={modal.selectionCount}
          entityLabel={modal.entityLabel}
          pending={pending}
          onCancel={closeModal}
          onConfirm={(mode: DemoBidMode, value: number) => {
            const modeLabel =
              mode === 'SET'
                ? `set to ${value}`
                : mode === 'INCREASE_PERCENT'
                  ? `+${value}%`
                  : mode === 'DECREASE_PERCENT'
                    ? `-${value}%`
                    : mode === 'INCREASE_AMOUNT'
                      ? `+${value}`
                      : `-${value}`;
            finish(
              `Updated bid (${modeLabel}) for ${modal.selectionCount} ${modal.entityLabel}${modal.selectionCount === 1 ? '' : 's'} (demo)`
            );
          }}
        />
      );
    }

    if (modal.kind === 'change_status') {
      return (
        <WorkspaceDemoStatusModal
          selectionCount={modal.selectionCount}
          entityLabel={modal.entityLabel}
          pending={pending}
          onCancel={closeModal}
          onConfirm={(state) => {
            const verb = state === 'ENABLED' ? 'Enabled' : 'Paused';
            finish(
              `${verb} ${modal.selectionCount} ${modal.entityLabel}${modal.selectionCount === 1 ? '' : 's'} (demo)`
            );
          }}
        />
      );
    }

    if (modal.kind === 'set_portfolio') {
      return (
        <WorkspaceDemoPortfolioModal
          selectionCount={modal.selectionCount}
          portfolios={portfolios}
          pending={pending}
          onCancel={closeModal}
          onConfirm={(portfolioId) => {
            const name = portfolios.find((p) => p.id === portfolioId)?.name ?? portfolioId;
            finish(
              `Set portfolio "${name}" on ${modal.selectionCount} campaign${modal.selectionCount === 1 ? '' : 's'} (demo)`
            );
          }}
        />
      );
    }

    if (modal.kind === 'add_to_campaign') {
      return (
        <WorkspaceDemoAddToCampaignModal
          selectionCount={modal.selectionCount}
          campaigns={campaigns}
          adGroups={adGroups}
          pending={pending}
          onCancel={closeModal}
          onConfirm={(campaignId, adGroupId) => {
            const campaignName = campaigns.find((c) => c.id === campaignId)?.name ?? campaignId;
            const adGroupName = adGroups.find((a) => a.id === adGroupId)?.name ?? adGroupId;
            finish(
              `Added ${modal.selectionCount} search term${modal.selectionCount === 1 ? '' : 's'} to ${campaignName} / ${adGroupName} (demo)`
            );
          }}
        />
      );
    }

    return null;
  }, [modal, pending, closeModal, finish, campaigns, adGroups, portfolios]);

  return { openDemoAction, demoModals };
}
